import React from 'react';
import { observer } from 'mobx-react';
import { useRouter } from 'next/router';
import moment from 'moment';
import { routes } from 'env/routes';
import { isWeekend } from 'env/utils/dateUtil';
import LoadingSpinner from 'ui/components/LoadingSpinner';
import { useHasFeature } from '@portal/env/hooks/useHasFeature';
import { useMobxStores } from '@portal/env/hooks/useMobxStores';
import { useMutation } from '@tanstack/react-query';
import { INexudusCoworker } from '@portal/env/services/nexudus/types/Coworker';
import { BasketBookingData } from '@portal/types/nexudus';
import { refreshFullCalendar } from '@bw-ui/lib/refreshFullCalendar';
import { isResourceRltGated } from '@portal/env/utils/rltGate';

interface BookingFormToolBarProps {
  close: () => void;
  updateBooking: (key: string, value: any) => void;
  showBookingModal: () => void;
  hideBookingModal: () => void;
  componentName?: string;
  /**
   * If true, we should show different wording because the user is editing their basket rather than creating a new booking.
   */
  isEditingBasket?: boolean;
  onCancelBooking?: () => void;
}

const useAddSplitBookingToBasket = (hasStripeCheckout: boolean) => {
  const { appStore, authStore, bookingsStore, checkoutStore } = useMobxStores();

  return useMutation({
    mutationKey: ['addSplitBasketToBooking'],
    mutationFn: async ([data, fromTimeString, toTimeString]: [
      data: BasketBookingData,
      fromTimeString: string,
      toTimeString: string,
    ]) => {
      const fromTime = moment(fromTimeString);
      const toTime = moment(toTimeString);
      const items =
        checkoutStore.basket.length +
        moment(toTime).diff(moment(fromTime), 'days');

      if (!data) {
        throw new Error('Booking is not set');
      }
      if (items > 30) {
        await appStore.setPopMessage(
          'You can request a maximum of 30 bookings in a single transaction',
        );
        throw new Error(
          'You can request a maximum of 30 bookings in a single transaction',
        );
      }

      for (
        let date = fromTime;
        moment(date).startOf('day') <= moment(toTime).startOf('day');
        date = moment(date).clone().add(1, 'day')
      ) {
        const thisFromTime = moment(date).clone().toDate();
        const thisToTime = moment(date).clone().toDate();

        thisFromTime.setHours(fromTime.hours());
        thisFromTime.setMinutes(fromTime.minutes());
        thisToTime.setHours(toTime.hours());
        thisToTime.setMinutes(toTime.minutes());

        const includeWeekends = isWeekend(fromTime) || isWeekend(toTime);

        if (!includeWeekends && isWeekend(date)) continue;

        const booking = {
          ...data,
          Id: null,
          FromTime: thisFromTime,
          ToTime: thisToTime,
          ResourceName: data.ResourceName,
          ResourceId: data.ResourceId,
          Notes: data.Notes,
          BookingVisitors: data.BookingVisitors,
          SelectedShift: data.SelectedShift,
          IncludeZoomInvite: data.IncludeZoomInvite,
        };

        const price = await bookingsStore.loadBookingPriceDebounced(booking);

        checkoutStore.addToBasket({
          type: 'booking',
          data: booking,
          resourceProducts: bookingsStore.resourceProducts,
          bookingPrice: price,
          previewInvoice: false,
        });
      }

      // Only fire store-level preview for legacy checkout.
      // Checkout's useInvoicePreview hook owns the preview lifecycle there.
      if (authStore.isLoggedIn && !hasStripeCheckout)
        return checkoutStore.loadInvoicePreview();
    },
  });
};

export const useAddToBasket = (
  props: Pick<BookingFormToolBarProps, 'hideBookingModal' | 'close'>,
) => {
  const { authStore, bookingsStore, checkoutStore } = useMobxStores();
  const hasStripeCheckout = useHasFeature('featureStripePayments');

  const addSplitBookingToBasket = useAddSplitBookingToBasket(hasStripeCheckout);
  return useMutation({
    mutationKey: ['addToBasket'],
    mutationFn: async ([goToBasket]: [goToBasket: boolean]) => {
      const { isLoadingBookingPrice, resourceProducts, bookingPrice } =
        bookingsStore;

      const booking = bookingsStore.booking as null | BasketBookingData;

      if (!booking) throw new Error('Booking is not set');

      if (goToBasket) {
        window.open(
          hasStripeCheckout ? routes.checkout : routes.spreedlycheckout,
          '_top',
        );
      }

      const { customer } = authStore;
      if (isLoadingBookingPrice) return;

      const resource = bookingsStore.resources.find(
        (r) => r.Id === booking.ResourceId,
      );
      const hasShift = resource.Shifts?.length > 0;

      if (hasShift) {
        await addSplitBookingToBasket.mutateAsync([
          booking,
          booking.FromTime,
          booking.ToTime,
        ]);
      } else {
        checkoutStore.addToBasket({
          type: 'booking',
          data: booking,
          bookingPrice: bookingPrice,
          resourceProducts: resourceProducts,
          previewInvoice: !hasStripeCheckout && customer != null,
        });
      }

      props.hideBookingModal();
      bookingsStore.closeNewBooking();
      props.close();
    },
  });
};

const useSaveBooking = (
  props: Pick<
    BookingFormToolBarProps,
    'close' | 'showBookingModal' | 'hideBookingModal'
  >,
) => {
  const { appStore, authStore, bookingsStore, checkoutStore } = useMobxStores();
  const router = useRouter();

  const hasStripeCheckout = useHasFeature('featureStripePayments');
  const addSplitBookingToBasket = useAddSplitBookingToBasket(hasStripeCheckout);
  return useMutation({
    mutationKey: ['saveBooking'],
    mutationFn: async () => {
      const customer = authStore.customer as null | INexudusCoworker;
      if (!customer) throw new Error('Must be logged in to save booking');
      const { configuration } = appStore;
      const cancelUnpaidMembers =
        configuration['Bookings.CancelUnPaidBookingsForSubscribers'];
      const cancelUnpaidContacts =
        configuration['Bookings.CancelUnPaidBookingsForNonSubscribers'];
      const isMember = customer.IsMember;

      if (bookingsStore.isLoadingBookingPrice)
        throw new Error('Booking price is not loaded');

      const booking = bookingsStore.booking as null | BasketBookingData;
      if (!booking) throw new Error('Booking is not set');

      //New booking
      if (!(booking.Id && booking.Id > 0)) {
        // Capture store values before navigation — router.push triggers
        // the checkout page's BookingDetailsModal to mount, which calls
        // loadResourceProducts and clears bookingsStore.resourceProducts.
        const { resourceProducts, bookingPrice } = bookingsStore;

        await router.push(
          hasStripeCheckout ? routes.checkout : routes.spreedlycheckout,
        );

        const resource = bookingsStore.resources.find(
          (r) => r.Id === booking.ResourceId,
        );
        const hasShift = resource.Shifts?.length > 0;

        if (hasShift)
          await addSplitBookingToBasket.mutateAsync([
            booking,
            booking.FromTime,
            booking.ToTime,
          ]);
        else {
          checkoutStore.addToBasket({
            type: 'booking',
            data: booking,
            bookingPrice: bookingPrice,
            resourceProducts: resourceProducts,
            previewInvoice: !hasStripeCheckout && customer != null,
          });
        }

        props.close();
        props.hideBookingModal();
        bookingsStore.closeNewBooking();

        return;
      }
      try {
        const result = await bookingsStore.saveBooking();
        try {
          await bookingsStore.loadUpcomingBookings();
        } catch {
          // swallow exceptions in this case.
        }

        props.hideBookingModal();
        await appStore.setPopMessage(result.Message);

        if (result.Status !== 200) props.showBookingModal();
        else {
          props.close();

          if (
            (isMember && cancelUnpaidMembers) ||
            (!isMember && cancelUnpaidContacts)
          ) {
            await router.push(routes.invoices);
          }
        }
        refreshFullCalendar();
      } catch {
        props.hideBookingModal();
        await appStore.setPopMessage(
          'Could not save this booking. Please try again later.',
        );
        props.showBookingModal();
      }
    },
  });
};

export const BookingFormToolBar = observer((props: BookingFormToolBarProps) => {
  const { appStore, authStore, bookingsStore } = useMobxStores();

  const isClientSideIframe = window.location !== window.parent.location;

  const { updateBooking, showBookingModal, hideBookingModal, close } = props;

  const booking = bookingsStore.booking as null | BasketBookingData;

  const resource = booking?.ResourceId
    ? bookingsStore.resources.find((r) => r.Id === booking.ResourceId)
    : null;

  const customer = authStore.customer as null | INexudusCoworker;
  const rltGated = isResourceRltGated(booking?.ResourceId, customer?.Custom10);

  const bookingHasServerId = booking?.Id && booking?.Id > 100;
  const bookingRequiresConfirmation =
    bookingHasServerId && bookingsStore.hasBookingChanged;
  const { isLoadingBookingPrice } = bookingsStore;

  const addToBasket = useAddToBasket({ hideBookingModal, close });
  const saveBooking = useSaveBooking({
    close,
    showBookingModal,
    hideBookingModal,
  });
  const { configuration } = appStore;

  const isSavingBooking = addToBasket.isPending || saveBooking.isPending;
  const addToBasketButton = (
    <button
      disabled={isLoadingBookingPrice || isSavingBooking}
      onClick={() => addToBasket.mutate([isClientSideIframe])}
      className="btn btn-outline d-flex w-100 mt-8"
      type="button"
    >
      {props.isEditingBasket ? 'Update' : 'Add to basket'}
    </button>
  );
  if (!booking) return <></>;
  return (
    <div
      data-component-name={props.componentName}
      className="booking-form-tool-bar"
    >
      {!appStore.corporateDashboard && (
        <>
          {authStore.isLoggedIn &&
          booking.Id &&
          booking.Id > 0 &&
          configuration['Bookings.RequestDrafts'] ? (
            <div className="mt-12">
              <div className="custom-control custom-checkbox">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="proposal"
                  checked={booking.Tentative}
                  onChange={(ev) =>
                    updateBooking('Tentative', ev.target.checked)
                  }
                />
                <label
                  className="custom-control-label fs-12 pt-3"
                  htmlFor="proposal"
                >
                  I am not ready to book, send me a proposal with the details of
                  this booking instead.
                </label>
              </div>
            </div>
          ) : null}
        </>
      )}
      <div className="mt-24 confirm-booking-button">
        {rltGated && (
          <div className="alert alert-warning rlt-gated-message">
            {
              'Please complete the Red Light Therapy Par-Q form in the Bruntwood app to enable booking access.'
            }
          </div>
        )}

        {!rltGated &&
        resource &&
        authStore.isLoggedIn &&
        !booking.inBasket &&
        !bookingHasServerId ? (
          <button
            onClick={() => saveBooking.mutate()}
            disabled={isLoadingBookingPrice || isSavingBooking}
            className="btn d-flex w-100 mt-8"
            type="button"
          >
            {!isSavingBooking &&
              !booking.Tentative &&
              !resource.RequiresConfirmation && (
                <span>{'Confirm this booking'}</span>
              )}

            {!isSavingBooking &&
              !booking.Tentative &&
              resource.RequiresConfirmation && <span>{'Request booking'}</span>}

            {!isSavingBooking && booking.Tentative && (
              <span>{'Send proposal'}</span>
            )}
            {isSavingBooking && <LoadingSpinner />}
          </button>
        ) : null}

        {!rltGated && !booking.inBasket && bookingRequiresConfirmation ? (
          <button
            onClick={() => saveBooking.mutate()}
            disabled={isLoadingBookingPrice || isSavingBooking}
            className="btn d-flex w-100 mt-8"
            type="button"
          >
            {!isSavingBooking && <span>{'Confirm this booking'}</span>}
            {isSavingBooking && <LoadingSpinner />}
          </button>
        ) : null}

        {!rltGated && (!authStore.isLoggedIn || booking.inBasket)
          ? addToBasketButton
          : null}

        <a
          onClick={(ev) => {
            ev.preventDefault();
            bookingsStore.resetBookingChangeTracking();
            props.close();
          }}
          href="#"
          className="btn btn-outline d-flex w-100 mt-8 close-booking-button"
        >
          {'Close'}
        </a>

        {!rltGated && authStore.isLoggedIn && booking.Id == 0
          ? addToBasketButton
          : null}

        {!booking.inBasket && booking.Id && booking.Id > 0 ? (
          <button
            disabled={isLoadingBookingPrice || isSavingBooking}
            onClick={() => props.onCancelBooking?.()}
            className="btn d-flex w-100 mt-8 btn-outline cancel-booking-button"
            type="button"
          >
            {'Cancel this booking'}
          </button>
        ) : null}
      </div>
    </div>
  );
});

export default BookingFormToolBar;
